"use client";

import { useCallback, useEffect, useRef } from "react"
import { createPortal } from "react-dom"

import { TiptapEditorShell } from "@/tiptap/shell/tiptap-editor-shell"
import { fullMode } from "@/tiptap/modes/full-mode"
import { useArticleWriter } from "@/tiptap/article-writer/use-article-writer"

const articleMode = {
  ...fullMode,
  toolbar: {
    ...fullMode.toolbar,
    showThemeToggle: false,
  },
}

export function ArticleWriter(props) {
  const {
    activeUserName,
    closeUrl,
    folderOptions,
    id,
    initialFocusTarget = "title",
    takeoverUrl,
  } = props

  const titleInputRef = useRef(null)

  const {
    autosavedAt,
    content,
    editorVersion,
    handleEditorChange,
    handleFolderChange,
    handleTitleChange,
    hasDraft,
    htmlContent,
    isDirty,
    majorError,
    publish,
    restored,
    save,
    selectedFolder,
    title,
    unrestore,
  } = useArticleWriter(props)

  const handleEditorReady = useCallback((editor) => {
    if (initialFocusTarget !== "editor") return

    requestAnimationFrame(() => {
      editor?.commands?.focus?.("start")
    })
  }, [initialFocusTarget])

  useEffect(() => {
    if (initialFocusTarget !== "title") return

    titleInputRef.current?.focus()
  }, [initialFocusTarget])

  return (
    <>
      <div className="drafter__major-error">
        {majorError ? (
          <div className="drafter__major-error__box">
            <p><i className="fal fa-exclamation-triangle" /> An error occurred saving this draft. Please copy the following HTML to an <b>external editor</b> to prevent losing unpublished changes.</p>
            <textarea value={htmlContent} style={{ height: "150px" }} readOnly />
            <a href="#" className="button--primary button" onClick={(event) => {
              event.preventDefault()
              event.stopPropagation()
              save()
            }}><i className="fas fa-redo" />Re-attempt Save</a>
          </div>
        ) : null}
      </div>

      <div className="drafter">
        <main className="drafter__content">
          <div className="form-group">
            <label htmlFor="articleTitle" className="required">Name</label>
            <input
              ref={titleInputRef}
              value={title}
              onChange={handleTitleChange}
              required
              type="text"
              id="articleTitle"
              placeholder="How to..."
              className="drafter__title" />
          </div>

          <div className="writer-wrap writer--article">
            <input type="hidden" value={title} name="article[name]" />
            <input type="hidden" value={content} name="article[content]" />

            <div className="drafter__writer">
              <div className="form-group tiptap-editor-shell">
                <TiptapEditorShell
                  key={`article-editor-${editorVersion}`}
                  content={content}
                  mode={articleMode}
                  recordType="Article"
                  recordId={id}
                  onChange={handleEditorChange}
                  onReady={handleEditorReady} />
              </div>
            </div>
          </div>
        </main>

        <div className="drafter__finish">
          <div className="drafter__autosave">
            <h3><b>Last autosaved:</b> <span className="autosaved-date">{autosavedAt}</span></h3>
            {restored && hasDraft ? <h3>Restored from draft</h3> : null}
          </div>

          {restored && hasDraft ? (
            <header className="drafter__finish__header drafter__finish__header--bordered">
              <a href="#" onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
                unrestore()
              }} className="button button--secondary">Revert to Published Version</a>
            </header>
          ) : null}

          <header className="drafter__finish__header">
            <a
              href="#"
              className="button button--primary"
              onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
                publish()
              }}>Publish</a>
            {isDirty ? (
              <a
                href="#"
                className="button button--secondary"
                onClick={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                  save()
                }}>Save Draft</a>
            ) : null}
            <a className="button button--secondary" href={closeUrl}>Close</a>
          </header>

          <main>
            <div className="asset-sidebar__group">
              <header><h2>Meta</h2></header>
              <div className="asset-sidebar__group-form">
                <div className="form-group">
                  <label htmlFor="articleFolderId">Folder</label>
                  <select
                    name="article[folder_id]"
                    id="articleFolderId"
                    value={selectedFolder ?? ""}
                    className="form-control"
                    onChange={handleFolderChange}>
                    <option value="">No folder</option>
                    {folderOptions.map((option) => (
                      <option key={option[1]} value={option[1]}>
                        {option[0]}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>

      {activeUserName && typeof document !== "undefined" ? createPortal(
        <div className="manage_modal__wrapper">
          <div className="manage_modal manage_modal--confirm">
            <header>
              <h1>{activeUserName} is currently editing</h1>
              <a href={closeUrl} className="close">
                <i className="fas fa-times" />
              </a>
            </header>
            <div className="manage__modal__body">
              <p>You may take over editing, but any unsaved changes made by the current editor will be lost.</p>
              <div className="button-group" style={{ marginLeft: "auto" }}>
                <a className="button button--secondary" href={closeUrl}>Cancel</a>
                <a href={takeoverUrl} className="button button--danger">Take Over Editing</a>
              </div>
            </div>
          </div>
        </div>,
        document.body,
      ) : null}
    </>
  )
}

export default ArticleWriter
